import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import "../App.css";
import AnimatedLogo from "../components/AnimatedLogo";
import Sidebar from "../components/Sidebar";
import LanguageSwitcher from "../components/LanguageSwitcher";
import FloatingArrow from "../components/FloatingArrow";

function HomePageContainer({ heightPx, children }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: heightPx ? `${heightPx}px` : "100vh",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

// HomePage runs on every visit to "/"
// onNavigate still comes from App
function HomePage({ onNavigate, onLogoAnimationComplete, fontsReady = true }) {
  const { t, i18n } = useTranslation();
  const [uiReady, setUiReady] = useState(false);
  const navigatingRef = useRef(false);


  // lock viewport height so animation doesn't get a moving target on mobile
  const [vhPx, setVhPx] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0
  );

  // gate that prevents the logo animation from firing until layout is stable
  const [stableLayout, setStableLayout] = useState(false);

  useEffect(() => {
    const stop = () => window.lenis?.stop?.();
    const start = () => window.lenis?.start?.();
    stop();

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlHeight = document.documentElement.style.height;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.height = "100vh";
    document.documentElement.style.height = "100vh";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.height = prevBodyHeight;
      document.documentElement.style.height = prevHtmlHeight;
      start();
    };
  }, []);

  useEffect(() => {
    if (!i18n.isInitialized) return;
    document.title = t("meta.title");
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t("meta.description"));
    document.documentElement.lang = i18n.language;
  }, [t, i18n.language, i18n.isInitialized]);

  useEffect(() => {
    const goNext = () => {
      if (navigatingRef.current) return;
      navigatingRef.current = true;
      onNavigate?.("/about");
    };

    let touchStartY = 0;

    const onWheel = (event) => {
      if (event.deltaY > 12) goNext();
    };

    const onTouchStart = (event) => {
      touchStartY = event.touches?.[0]?.clientY || 0;
    };

    const onTouchMove = (event) => {
      const currentY = event.touches?.[0]?.clientY || touchStartY;
      if (touchStartY - currentY > 42) goNext();
    };

    const onKeyDown = (event) => {
      if (["ArrowDown", "PageDown", " "].includes(event.key)) {
        goNext();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onNavigate]);


  useLayoutEffect(() => {
    if (!fontsReady) return undefined;

    let cancelled = false;
    const h = window.innerHeight;
    setVhPx(h);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setStableLayout(true);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [fontsReady]);

  return (
    <HomePageContainer heightPx={vhPx}>
      {/* AnimatedLogo only starts its entrance when stableLayout === true.
         You need to forward this into AnimatedLogo so it doesn't play early.
         We'll add a new prop "readyToAnimate".
      */}
      <AnimatedLogo
        readyToAnimate={stableLayout}
        onLogoComplete={() => {
          setUiReady(true);
          onLogoAnimationComplete?.();
        }}
      />

      {uiReady && <LanguageSwitcher />}
      {uiReady && <Sidebar onNavigate={onNavigate} />}
      {uiReady && <FloatingArrow onClick={() => onNavigate("/about")} />}
    </HomePageContainer>

  );
}

export default HomePage;
